import { useEffect, useState } from "react";

import { FileThumbnailProps, getFileConfig } from "../../../types/files";
import { getPreviewMeta } from "../../../utils/filePreview";
import Icon from "../Icon";

// Compact, non-interactive stand-in for FilePreview — used in thumbnail-sized slots (e.g. the
// FileUploadDropzone attachment card) where the actual file content, not just a generic file-type
// icon, is useful at a glance. Falls back to the file-type icon for kinds it can't render small
// (audio/video/office docs).
export default function FileThumbnail({
  file,
  className = "",
  iconClassName = "",
}: FileThumbnailProps) {
  const { name, src, kind } = getPreviewMeta(file);
  const fileConfig = getFileConfig({ name } as File);

  const [csvPreview, setCsvPreview] = useState<string | null>(null);

  useEffect(() => {
    if (kind !== "csv" || !src) return undefined;

    let cancelled = false;
    setCsvPreview(null);

    fetch(src)
      .then((res) => res.text())
      // only the first few lines fit in a thumbnail-sized box
      .then((text) => {
        if (!cancelled) setCsvPreview(text.split("\n").slice(0, 6).join("\n"));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [kind, src]);

  if (kind === "image" && src) {
    return <img src={src} alt={name} className={`object-cover ${className}`} />;
  }

  if (kind === "pdf" && src) {
    return (
      <iframe
        src={src}
        title={name}
        tabIndex={-1}
        className={`pointer-events-none bg-white ${className}`}
      />
    );
  }

  if (kind === "csv" && csvPreview) {
    return (
      <pre
        className={`bg-white text-neutral-900 overflow-hidden text-[6px] leading-[7px] whitespace-pre p-1 ${className}`}
      >
        {csvPreview}
      </pre>
    );
  }

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <Icon
        name={fileConfig?.icon}
        className={iconClassName || "w-8 h-8 text-label-muted"}
      />
    </div>
  );
}
